/**
 * Gov Promotion actions — TEMPORARY workspace promotion endpoints (CT-5095).
 *
 * INTERIM SEAM: Work Queues uses `/v1/workspace/promotion/*` (Sandbox API) until
 * the unified My Work promotion endpoints ship (CT-4867). When those exist, replace
 * this module's list/preview/detail/claims paths with the my-work family and drop
 * the workspace fan-out in {@link aggregateWorkQueueEvents}.
 *
 * Contract today: Sandbox API (`sandbox.yaml`), base `/v1/workspace/promotion`.
 * Workspace context + the calling user come from the token — never in the body.
 *
 * Mirrors the proven beta client (web-app-governance-studio `src/services/promotion.js`).
 * Write actions use {@link postGsAction}; reads use GET + `validateApiResponse`.
 */

import { gsApi } from './apiClient.js';
import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { postGsAction } from './gsAction.js';

/** GET + envelope-unwrap. Returns the normalized `{ body, meta, ... }`. */
async function get(path, errorMessage) {
  const res = await gsApi.get(path);
  return validateApiResponse(res, errorMessage);
}

/**
 * List promotion packages (Task 47).
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=50]
 * @param {string} [params.sandboxUuid]
 * @param {string} [params.knowledgebaseUuid]
 * @param {{ keyword?: string }} [params.search]
 * @param {{ statuses?: string[], ownerUuid?: string }} [params.filters]
 * @returns {Promise<{ items: Object[], meta: Object|null }>}
 */
export async function listPromotions({
  page = 1,
  pageSize = 50,
  sandboxUuid,
  knowledgebaseUuid,
  search,
  filters,
} = {}) {
  const payload = { page, pageSize };
  if (sandboxUuid) payload.sandboxUuid = sandboxUuid;
  if (knowledgebaseUuid) payload.knowledgebaseUuid = knowledgebaseUuid;
  if (search) payload.search = search;
  if (filters) payload.filters = filters;
  const { body, meta } = await postGsAction('/v1/workspace/promotion/list', payload, 'Failed to load promotions');
  return { items: Array.isArray(body) ? body : [], meta: meta ?? null };
}

/**
 * Lightweight promotion card preview (Task 48).
 * @param {string} promotionUuid
 * @returns {Promise<Object|null>}
 */
export async function getPromotionPreview(promotionUuid) {
  if (!promotionUuid) return null;
  const { body } = await get(
    `/v1/workspace/promotion/preview/${promotionUuid}`,
    'Failed to load promotion preview',
  );
  return body ?? null;
}

/**
 * Full promotion details (Task 40).
 * @param {string} promotionUuid
 * @returns {Promise<Object|null>}
 */
export async function getPromotionDetails(promotionUuid) {
  if (!promotionUuid) return null;
  const { body } = await get(
    `/v1/workspace/promotion/details/${promotionUuid}`,
    'Failed to load promotion details',
  );
  return body ?? null;
}

/**
 * Candidate claims attached to a promotion (Task 39, paginated).
 * @param {string} promotionUuid
 * @param {Object} [params]
 * @returns {Promise<{ items: Object[], meta: Object|null }>}
 */
export async function listPromotionClaims(promotionUuid, { page = 1, pageSize = 100, orderBy, orderDirection } = {}) {
  if (!promotionUuid) return { items: [], meta: null };
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    orderBy: orderBy ?? 'added_at',
    orderDirection: orderDirection ?? 'DESC',
  });
  const { body, meta } = await get(
    `/v1/workspace/promotion/claims/${promotionUuid}?${query}`,
    'Failed to load promotion claims',
  );
  return { items: Array.isArray(body) ? body : [], meta: meta ?? null };
}

/**
 * Override per-promotion fields on a candidate claim (Task 43).
 * @param {string} candidateClaimUuid
 * @param {Object} patch - title, claimText, confidence, riskLevel, …
 */
export async function editCandidateClaim(candidateClaimUuid, patch = {}) {
  if (!candidateClaimUuid) return null;
  return postGsAction(
    `/v1/workspace/promotion/candidate-claim/edit/${candidateClaimUuid}`,
    patch,
    'Failed to update claim',
  );
}

/** Mark a candidate claim as reviewed (Task 44). */
export async function markCandidateClaimReviewed(candidateClaimUuid) {
  if (!candidateClaimUuid) return null;
  return postGsAction(
    `/v1/workspace/promotion/claim/mark-review/${candidateClaimUuid}`,
    {},
    'Failed to mark claim reviewed',
  );
}

/** Revert a candidate claim to pending review (Task 45). */
export async function unmarkCandidateClaimReviewed(candidateClaimUuid) {
  if (!candidateClaimUuid) return null;
  return postGsAction(
    `/v1/workspace/promotion/claim/unmark-review/${candidateClaimUuid}`,
    {},
    'Failed to unmark claim review',
  );
}

/**
 * Remove claims from a DRAFT promotion (Task 42).
 * Used when the reviewer rejects a claim at attestation time.
 */
export async function removeClaimsFromPromotion(promotionUuid, claimsUuids = []) {
  if (!promotionUuid || !claimsUuids.length) return null;
  return postGsAction(
    `/v1/workspace/promotion/claims/remove/${promotionUuid}`,
    { claimsUuids },
    'Failed to remove claims from promotion',
  );
}

/** Finalize a DRAFT promotion (Task 46). Requires all claims reviewed. */
export async function submitPromotion(promotionUuid) {
  if (!promotionUuid) return null;
  return postGsAction(
    `/v1/workspace/promotion/submit/${promotionUuid}`,
    {},
    'Failed to submit promotion',
  );
}
